import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Video, Plus, Trash2, GripVertical, Lock } from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import { getConsultationSettings, updateConsultationSettings } from '../../services/consultationService';
import { getVendorStatus } from '../../services/subscriptionService';
import { planAllowsPageKey } from '../../utils/vendorPlanAccess';
import Button from '../shared/Button';
import Input from '../shared/Input';
import CustomSelect from '../shared/CustomSelect';

const CONSULTATIONS_PAGE_KEY = 'page_consultations';

const QUESTION_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown' },
];

const ConsultationSettings = () => {
  const { error: showErrorToast, success: showSuccessToast } = useToastContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consultationEnabled, setConsultationEnabled] = useState(false);
  const [questions, setQuestions] = useState([]);

  const { data: statusPayload, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['vendor-subscription-status'],
    queryFn: getVendorStatus,
    staleTime: 60 * 1000,
  });

  const subscriptionReady = !subscriptionLoading && statusPayload?.success === true;
  const hasActiveSubscription = statusPayload?.data?.has_active_subscription === true;
  const vendorPageAccess = statusPayload?.data?.current_subscription?.vendor_page_access;
  const consultationsAllowedOnPlan = planAllowsPageKey(vendorPageAccess, CONSULTATIONS_PAGE_KEY);
  const canUseConsultations =
    subscriptionReady && hasActiveSubscription && consultationsAllowedOnPlan;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getConsultationSettings();
        if (res.success && res.data) {
          setConsultationEnabled(res.data.consultation_enabled ?? false);
          setQuestions(res.data.consultation_questions ?? []);
        }
      } catch (err) {
        showErrorToast('Error', 'Failed to load consultation settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // Intentionally run once on mount to avoid repeated GET loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { id: `q_${Date.now()}`, type: 'text', label: '', required: false, options: [], placeholder: '' },
    ]);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index, field, value) => {
    const next = [...questions];
    next[index] = { ...next[index], [field]: value };
    setQuestions(next);
  };

  const addOption = (qIndex) => {
    const next = [...questions];
    const opts = next[qIndex].options || [];
    opts.push('');
    next[qIndex].options = opts;
    setQuestions(next);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const next = [...questions];
    next[qIndex].options = next[qIndex].options || [];
    next[qIndex].options[optIndex] = value;
    setQuestions(next);
  };

  const removeOption = (qIndex, optIndex) => {
    const next = [...questions];
    next[qIndex].options = (next[qIndex].options || []).filter((_, i) => i !== optIndex);
    setQuestions(next);
  };

  const handleSave = async () => {
    if (!canUseConsultations) return;
    try {
      setSaving(true);
      await updateConsultationSettings({
        consultation_enabled: consultationEnabled,
        consultation_questions: questions.filter((q) => q.label?.trim()),
      });
      showSuccessToast('Success', 'Consultation settings saved');
    } catch (err) {
      showErrorToast('Error', err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || subscriptionLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-24 bg-gray-200 rounded" />
      </div>
    );
  }

  const upgradeMessage = !subscriptionReady
    ? null
    : !hasActiveSubscription
      ? 'Activate a subscription to enable consultation booking on your services.'
      : !consultationsAllowedOnPlan
        ? 'Your current plan does not include consultations. Upgrade to offer video consultations and custom questionnaires.'
        : null;

  return (
    <div className="space-y-6">
      {upgradeMessage && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Lock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
            <span>{upgradeMessage}</span>
          </div>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center justify-center font-medium text-amber-900 underline hover:text-amber-950 whitespace-nowrap"
          >
            View plans & upgrade
          </Link>
        </div>
      )}

      <div
        className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg ${
          !canUseConsultations ? 'opacity-70' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
            <Video className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Book Consultation</p>
            <p className="text-sm text-gray-500">Show &quot;Book Consultation&quot; button on your service pages</p>
          </div>
        </div>
        <label
          className={`relative inline-flex items-center ${canUseConsultations ? 'cursor-pointer' : 'cursor-not-allowed'}`}
        >
          <input
            type="checkbox"
            checked={consultationEnabled}
            disabled={!canUseConsultations}
            onChange={(e) => setConsultationEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50" />
        </label>
      </div>

      {consultationEnabled && (
        <div className={`border border-gray-200 rounded-lg p-4 ${!canUseConsultations ? 'opacity-70' : ''}`}>
          <h4 className="font-medium text-gray-900 mb-2">Consultation Questionnaire</h4>
          <p className="text-sm text-gray-500 mb-4">Clients will see these questions when booking a consultation.</p>

          <div className="space-y-4">
            {questions.map((q, idx) => (
              <div key={q.id || idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-2 mb-3">
                  <GripVertical className="w-4 h-4 text-gray-400 mt-2 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Question label"
                      value={q.label || ''}
                      onChange={(e) => updateQuestion(idx, 'label', e.target.value)}
                      disabled={!canUseConsultations}
                      className="text-sm"
                    />
                    <div className="flex gap-2 flex-wrap items-center">
                      <div className="min-w-[140px]">
                        <CustomSelect
                          options={QUESTION_TYPES}
                          value={q.type || 'text'}
                          onChange={(value) => updateQuestion(idx, 'type', value)}
                          placeholder="Select type"
                          size="sm"
                          disabled={!canUseConsultations}
                        />
                      </div>
                      <label className="flex items-center gap-1 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={!!q.required}
                          disabled={!canUseConsultations}
                          onChange={(e) => updateQuestion(idx, 'required', e.target.checked)}
                        />
                        Required
                      </label>
                    </div>
                    {q.type === 'select' && (
                      <div className="mt-2 space-y-1">
                        <span className="text-xs text-gray-500">Options (one per line):</span>
                        {(q.options || []).map((opt, oi) => (
                          <div key={oi} className="flex gap-1">
                            <Input
                              value={opt}
                              onChange={(e) => updateOption(idx, oi, e.target.value)}
                              placeholder={`Option ${oi + 1}`}
                              disabled={!canUseConsultations}
                              className="text-sm"
                            />
                        <button
                          type="button"
                          onClick={() => removeOption(idx, oi)}
                          disabled={!canUseConsultations}
                          className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-50 disabled:pointer-events-none"
                        >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addOption(idx)}
                          disabled={!canUseConsultations}
                          className="text-xs text-indigo-600 hover:underline disabled:opacity-50 disabled:pointer-events-none"
                        >
                          + Add option
                        </button>
                      </div>
                    )}
                    {q.type !== 'select' && (
                      <Input
                        placeholder="Placeholder (optional)"
                        value={q.placeholder || ''}
                        onChange={(e) => updateQuestion(idx, 'placeholder', e.target.value)}
                        disabled={!canUseConsultations}
                        className="text-sm"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeQuestion(idx)}
                    disabled={!canUseConsultations}
                    className="p-2 text-red-500 hover:bg-red-50 rounded disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addQuestion}
            disabled={!canUseConsultations}
            className="mt-4 flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            <Plus className="w-4 h-4" />
            Add question
          </button>
        </div>
      )}

      <Button onClick={handleSave} loading={saving} disabled={saving || !canUseConsultations}>
        Save Consultation Settings
      </Button>
    </div>
  );
};

export default ConsultationSettings;
